/**
 * Arrivées imprévues — the syndic's review of visitors who turned up with no
 * pass.
 *
 * Why this is a page and not a filter on /activite: an arrivée that was
 * refused, expired, or that the visitor walked away from writes NO
 * `entry_event`. Nobody entered and nobody was turned away, and inventing an
 * event for a non-event is how a log starts lying — so /activite genuinely
 * cannot show those rows. This is the only surface on which a request that
 * came to nothing is visible at all, which is most of the value: « on a sonné
 * chez le 12 à 23h et personne n'a répondu » is not in any other list.
 *
 * The two columns that must not be conflated are Décision and Issue.
 * Décision is what the RÉSIDENT tapped and is attributable to a person; Issue
 * is what the GARDIEN recorded at the gate. They are deliberately separate
 * facts everywhere in this feature, and a row reading « — · Admis » with an
 * amber Raison is precisely the case the syndic came here to find.
 */
import type { RouterOutputs } from "@gately/api";
import type { ColumnDef } from "@gately/ui/data-table";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { z } from "zod/v4";

import { DataTable } from "@gately/ui/data-table";
import { DateTimePicker } from "@gately/ui/date-time-picker";
import { StatusBadge } from "@gately/ui/status-badge";
import { Switch } from "@gately/ui/switch";
import {
  idSuffixLabel,
  spotArrivalIssue,
  spotArrivalIssueLabel,
} from "@gately/validators";

import { dateKey } from "~/lib/dashboard-format";
import { orpc } from "~/lib/orpc";
import {
  reviewDecisionLabel,
  unitFilterOptions,
} from "~/lib/spot-arrival-review";
import { resolvePermissions } from "~/lib/use-permissions";
import { useTerms } from "~/lib/use-terms";

const DATE_KEY = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

/** `flagged` is a boolean, so it is `?flagged=true` rather than a faceted
 *  param — same contract as /repertoire's tag switches. A filtered view stays
 *  shareable and survives a reload. */
const searchSchema = z.object({
  from: DATE_KEY.optional().catch(undefined),
  to: DATE_KEY.optional().catch(undefined),
  flagged: z.boolean().optional().catch(undefined),
});

type ReviewSearch = z.infer<typeof searchSchema>;

export const Route = createFileRoute("/_dashboard/arrivees-imprevues")({
  validateSearch: (s) => searchSchema.parse(s),
  // Same gate as /activite, and for the same reason: this is org-wide gate
  // activity, which a résidence can withhold from its own syndic. The server
  // enforces it too (`listForSyndic` is an adminProcedure) — hiding the page
  // is a courtesy, never the boundary.
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(
      orpc.me.get.queryOptions(),
    );
    if (!resolvePermissions(me).canViewActivity) {
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({ to: "/" });
    }
  },
  component: ArriveesImprevuesPage,
});

type ReviewRow = RouterOutputs["spotArrival"]["listForSyndic"]["rows"][number];

/** Date keys go to the server as-is: it turns them into the ORGANIZATION's
 *  midnights (`dateKeyBounds`). The browser's own zone is not the building's. */
function rangeInput(search: ReviewSearch) {
  return {
    ...(search.from ? { from: search.from } : {}),
    ...(search.to ? { to: search.to } : {}),
    flaggedOnly: Boolean(search.flagged),
  };
}

function ArriveesImprevuesPage() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const terms = useTerms();

  const input = rangeInput(search);
  const query = useQuery(
    orpc.spotArrival.listForSyndic.queryOptions({ input }),
  );

  // Data first, error second. React Query keeps `data` through a FAILED
  // REFETCH, so testing `isError` first would blank a fully-loaded table on a
  // transient blip — the same ordering rule DetailSheet's `state` follows.
  const rows = query.data?.rows ?? [];
  const failed = query.data === undefined && query.isError;

  function setFilter(patch: Partial<ReviewSearch>) {
    void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true });
  }

  const columns: ColumnDef<ReviewRow>[] = [
    {
      accessorKey: "createdAt",
      size: 150,
      minSize: 130,
      enableHiding: false,
      meta: { label: "Date" },
      header: "Date",
      cell: ({ getValue }) => (
        <span className="text-muted-foreground whitespace-nowrap">
          {new Date(getValue<string>()).toLocaleString("fr-FR", {
            day: "2-digit",
            month: "2-digit",
            year: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
          })}
        </span>
      ),
    },
    {
      accessorKey: "visitorName",
      size: 200,
      minSize: 150,
      enableHiding: false,
      meta: { label: "Visiteur" },
      header: "Visiteur",
    },
    {
      // The company alone. Who was visited is its own column below, so the
      // syndic can sort and filter by company without « pour … » in the way.
      // Exact match: « Glovo » must not also select « Glovo Maroc ».
      accessorKey: "unitLabel",
      size: 180,
      minSize: 130,
      filterFn: "equalsString",
      meta: {
        label: terms.unitRowLabel,
        filter: { kind: "select", options: unitFilterOptions(rows) },
      },
      header: terms.unitRowLabel,
    },
    {
      accessorKey: "hostLabel",
      size: 170,
      minSize: 130,
      // « Personne visitée » (office) / « Hôte » (résidence).
      meta: { label: terms.hostRowLabel },
      header: terms.hostRowLabel,
      cell: ({ getValue }) =>
        getValue<string | null>() ?? (
          <span className="text-muted-foreground">—</span>
        ),
    },
    {
      accessorKey: "purposeLabel",
      size: 130,
      minSize: 110,
      meta: { label: "Motif" },
      header: "Motif",
      cell: ({ getValue }) => (
        <StatusBadge tone="neutral" dot={false}>
          {getValue<string>()}
        </StatusBadge>
      ),
    },
    {
      accessorKey: "source",
      size: 150,
      minSize: 120,
      meta: { label: "Provenance" },
      header: "Provenance",
      // Whose word the row rests on. The words differ per vertical — a
      // résidence has résidents and concierges, an office has employés and
      // agents d'accueil — so they come from Terms, never from a literal.
      cell: ({ getValue }) => (
        <span className="text-muted-foreground">
          {getValue<ReviewRow["source"]>() === "resident"
            ? terms.residentLabel
            : terms.guardLabel}
        </span>
      ),
    },
    {
      accessorKey: "decision",
      size: 130,
      minSize: 110,
      meta: { label: "Décision" },
      header: "Décision",
      // What the person visited answered, and nothing else. « — » means
      // nobody answered a request that is over, which is a different
      // statement from « Refusé ».
      cell: ({ row }) => {
        const v = row.original.decision;
        return (
          <span
            className={
              v === "approved"
                ? "text-(--success-700)"
                : v === "rejected"
                  ? "text-destructive"
                  : v === "leave_at_desk"
                    ? "text-foreground"
                    : "text-muted-foreground"
            }
          >
            {reviewDecisionLabel(row.original, terms)}
          </span>
        );
      },
    },
    {
      id: "issue",
      size: 140,
      minSize: 120,
      accessorFn: (r) =>
        spotArrivalIssue({ status: r.status, outcome: r.outcome }),
      meta: { label: "Issue" },
      header: "Issue",
      // What the GARDIEN recorded at the gate.
      cell: ({ getValue }) => {
        const issue = getValue<ReturnType<typeof spotArrivalIssue>>();
        return (
          <span
            className={
              issue === "admitted"
                ? "text-(--success-700)"
                : issue === "turned_away"
                  ? "text-destructive"
                  : "text-muted-foreground"
            }
          >
            {spotArrivalIssueLabel(issue, terms)}
          </span>
        );
      },
    },
    {
      accessorKey: "reasonLabel",
      size: 210,
      minSize: 150,
      meta: { label: "Raison" },
      header: "Raison",
      // Amber only for `exceptional`. Flagging every phone confirmation would
      // flag nearly every arrival in week one and train the syndic to ignore
      // the flag — which costs exactly the signal it exists to carry. The
      // severity was graded server-side with the policy in force at CLOSE
      // time, so changing the setting never reclassifies history.
      cell: ({ row, getValue }) => {
        const label = getValue<string | null>();
        if (!label) return <span className="text-muted-foreground">—</span>;
        return row.original.severity === "exceptional" ? (
          <StatusBadge tone="warning">{label}</StatusBadge>
        ) : (
          <span className="text-muted-foreground">{label}</span>
        );
      },
    },
    {
      accessorKey: "idSuffix",
      size: 110,
      minSize: 90,
      meta: { label: "Pièce" },
      header: "Pièce",
      // Only ever the last three characters (GATELY-47). The « … » says so.
      cell: ({ getValue }) => (
        <span className="text-muted-foreground font-mono">
          {idSuffixLabel(getValue<string | null>()) ?? "—"}
        </span>
      ),
    },
    {
      accessorKey: "gardienName",
      size: 160,
      minSize: 130,
      meta: { label: terms.guardLabel },
      header: terms.guardLabel,
      cell: ({ getValue }) => getValue<string | null>() ?? "—",
    },
  ];

  const isFiltered =
    search.from !== undefined ||
    search.to !== undefined ||
    search.flagged === true;

  return (
    <div className="px-7 py-6">
      <div className="mb-5">
        <h1 className="text-foreground text-2xl font-semibold tracking-[-0.02em]">
          Arrivées imprévues
        </h1>
        <p className="text-muted-foreground mt-1 text-sm">
          Les visiteurs présentés sans pass {terms.ofTheOrg}, y compris ceux qui
          sont repartis ou restés sans réponse.
        </p>
      </div>

      <DataTable
        columns={columns}
        data={rows}
        isLoading={query.isPending}
        initialPinning={{ left: ["createdAt"] }}
        showColumnToggle
        showColumnFilters
        toolbar={
          <>
            <DateTimePicker
              mode="date"
              className="h-9 w-36"
              value={
                search.from ? new Date(`${search.from}T00:00:00`) : undefined
              }
              onChange={(d) => setFilter({ from: d ? dateKey(d) : undefined })}
              placeholder="Du"
            />
            <DateTimePicker
              mode="date"
              className="h-9 w-36"
              value={search.to ? new Date(`${search.to}T00:00:00`) : undefined}
              onChange={(d) => setFilter({ to: d ? dateKey(d) : undefined })}
              placeholder="Au"
            />
            {search.from || search.to ? (
              <button
                type="button"
                onClick={() => setFilter({ from: undefined, to: undefined })}
                className="text-muted-foreground hover:text-foreground text-sm font-medium transition-colors"
              >
                Effacer les dates
              </button>
            ) : null}
            <label className="flex items-center gap-2 text-sm">
              <Switch
                checked={Boolean(search.flagged)}
                onCheckedChange={(on) =>
                  setFilter({ flagged: on || undefined })
                }
              />
              Exceptionnels seulement
            </label>
          </>
        }
        empty={
          // A failed load must never read as « aucune arrivée » — that is the
          // one sentence a syndic would act on, and it would be a lie about
          // the résidence rather than about the network.
          failed
            ? {
                title: "Impossible de charger les arrivées imprévues.",
                description:
                  "Vérifiez votre connexion, puis rechargez la page. Si le problème persiste, contactez Gately.",
              }
            : isFiltered
              ? {
                  title: "Aucune arrivée ne correspond",
                  description: "Élargissez la période ou désactivez le filtre.",
                }
              : {
                  title: "Aucune arrivée imprévue enregistrée.",
                  description: `Chaque visiteur présenté sans pass apparaît ici dès que ${terms.guardLabel.toLowerCase()} en signale un.`,
                }
        }
        // The server decides this, not the row count: with the flag on, the
        // window can be truncated while very few rows come back.
        footer={
          query.data?.truncated ? (
            <>
              Période tronquée : seules les arrivées les plus récentes sont
              affichées. Resserrez les dates pour voir les plus anciennes.
            </>
          ) : null
        }
      />
    </div>
  );
}
